import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { appUpdate } from '@e3dc-react/shell/State/App/AppActions'
import { Box, Grid, useMediaQuery, useTheme } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useUser } from 'src/Hooks/useUser'
import { makeStyles } from 'tss-react/mui'
import Installations from '../../Libs/installations/components/Installations'
import { getCardVsTableView, onEditInstallation, onSelectInstallation } from 'src/Libs/installations/installation.functions'
import PrivateInstallationCard from 'src/Elements/Installations/PrivateInstallationCard'
import EntryPointMessage from 'src/Elements/Dashboard/EntryPointMessage'
import useWindowSize from '@e3dc-react/shell/Hooks/useWindowSize'
import { Installation } from 'src/Libs/installations/models/installation.model'
import useInstallationsContext from 'src/Libs/installations/hooks/use-installations-context'
import { BannerLocations, BannerQueue } from 'src/Libs/banners'
import { OWNER_OVERVIEW_INSTALLATION_TABLE_ID } from 'src/Libs/qa/ids'

const MIN_CARD_HEIGHT = 200
const MAX_CARD_HEIGHT = 400
const WELCOME_TOP_PADDING = 16
const COMPONENTS_PADDINGS = 44

interface StyleProps {
  maxCardHeight?: number
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  welcomeMessageContainer: {
    display: 'flex',
    alignItems: 'end',
    height: '100%',
    marginTop: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      marginTop: 0,
    },
  },
  installationTableView: {
    boxShadow: theme.effectStyles.shadowMain,
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.grey.grey06}`,
    marginTop: theme.spacing(2),
  },
  installationCardView: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  installationCardContainer: {
    height: props.maxCardHeight ?? MAX_CARD_HEIGHT,
    width: 'calc(50% - 6px)', // account for gap of theme.spacing(2) = 12px
    [theme.breakpoints.down('md')]: {
      height: MIN_CARD_HEIGHT,
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
}))

const OwnerOverview: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useCustomTranslation()
  const navigate = useNavigate()
  const [maxCardHeight, setMaxCardHeight] = useState<number>()
  const theme = useTheme()
  const downMd = useMediaQuery(theme.breakpoints.down('md'))
  const welcomeMessageRef = useRef<HTMLDivElement>(null)
  const { classes } = useStyles({ maxCardHeight })
  const { height } = useWindowSize()

  const { installationCount, installations } = useInstallationsContext()
  const { userData } = useUser()
  const viewType = getCardVsTableView(installationCount)

  const onSelect = (installation?: Installation): void => {
    onSelectInstallation(dispatch, navigate, installation)
  }

  const onEdit = (installation?: Installation): void => {
    onEditInstallation(dispatch, navigate, installation)
  }

  useEffect(() => {
    dispatch(appUpdate({ title: t('dashboard.overview.title') }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (welcomeMessageRef.current && height) {
      const welcomeMessageHeight = welcomeMessageRef.current?.clientHeight + WELCOME_TOP_PADDING
      const { height: headerHeight, marginBottom, padding, top } = theme.shape.header
      const tilesHeight = height - welcomeMessageHeight - headerHeight - marginBottom - padding - top - COMPONENTS_PADDINGS

      if (tilesHeight !== maxCardHeight && tilesHeight >= MIN_CARD_HEIGHT) {
        setMaxCardHeight(tilesHeight / 2)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, welcomeMessageRef, height])

  return (
    <Grid item xs={12} data-qa={OWNER_OVERVIEW_INSTALLATION_TABLE_ID}>
      <BannerQueue location={BannerLocations.OwnerOverview} sx={{ mb: downMd ? 2 : 0 }} />
      <Grid item xs={12} className={classes.welcomeMessageContainer} ref={welcomeMessageRef}>
        <EntryPointMessage firstName={userData?.Vorname ?? ''} />
      </Grid>
      {viewType === 'installation-table-view' && (
        <Grid item xs={12} className={classes.installationTableView}>
          <Installations type={'standard'} onEdit={onEdit} onSelect={onSelect} />
        </Grid>
      )}
      {viewType === 'installation-card-view' && (
        <Box className={classes.installationCardView}>
          {installations.map((installation, index) => (
            <Box key={index} className={classes.installationCardContainer}>
              <PrivateInstallationCard installation={installation} cardSize={downMd ? 'small' : 'large'} />
            </Box>
          ))}
        </Box>
      )}
    </Grid>
  )
}

export default OwnerOverview
